import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Button, Chip, Collapse, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useContext, useState } from 'react';
import { CoursesIds } from '../../../constants';
import { DialogsContext } from '../../../context/dialogsContext';
import { UserContext } from '../../../context/userContext';
import { CaseType, User } from '../../../types';
import ExpandableText from '../../common/ExpandableText';
import MetaField from '../../common/MetaField';
import { caseStateTone, formatCaseState, getReviewAccess } from '../caseState';
import CourseBadge, { getCourseAppearance } from '../CourseBadge';
import ReviewOwnership from '../ReviewOwnership';
import StatusHistory from '../StatusHistory';

interface ReviewCaseCardProps {
    reviewCase: CaseType;
    sessionCount: number;
}

const ReviewCaseCard = ({ reviewCase, sessionCount }: ReviewCaseCardProps) => {
    const { setReviewWorkspaceCase } = useContext(DialogsContext);
    const { user } = useContext(UserContext);
    const [historyOpen, setHistoryOpen] = useState(false);

    const access = getReviewAccess(reviewCase, user.id);
    const latestState = access.state;
    const healer = reviewCase.healer as User | undefined;
    const statuses = reviewCase.statuses ?? [];
    const lastActivity = statuses.length > 0 ? statuses[statuses.length - 1].created_date : reviewCase.created_date;

    return (
        <Paper
            variant='outlined'
            sx={{ borderRadius: 2, overflow: 'hidden', borderTop: '4px solid', borderTopColor: getCourseAppearance(reviewCase.cohort?.course?.id).background }}
        >
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent='space-between'
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                            <CourseBadge course={reviewCase.cohort?.course} />
                            <Typography variant='subtitle1' sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                {`Case ${reviewCase.id} · ${reviewCase.name || 'Untitled'}`}
                            </Typography>
                            <Chip
                                size='small'
                                label={formatCaseState(latestState)}
                                color={caseStateTone(latestState)}
                                variant='outlined'
                            />
                        </Stack>
                        <Typography variant='caption' color='text.secondary' sx={{ overflowWrap: 'anywhere' }}>
                            {`${reviewCase.cohort?.course?.name ?? '—'} (${reviewCase.cohort?.name ?? '—'}) · ${sessionCount} sessions · last activity ${DateTime.fromISO(lastActivity).toLocaleString(DateTime.DATE_MED)}`}
                        </Typography>
                        <ReviewOwnership access={access} />
                    </Box>
                    <Button
                        variant={access.isMine ? 'contained' : 'outlined'}
                        endIcon={<ArrowForwardRoundedIcon />}
                        onClick={() => setReviewWorkspaceCase(reviewCase)}
                        sx={{ flexShrink: 0 }}
                    >
                        {access.isMine ? 'Continue review' : 'Open case'}
                    </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                        <MetaField label='Healer' value={healer?.full_name} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <MetaField label='Type' value={reviewCase.type} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <MetaField label='Remote' value={reviewCase.remote === 'Y' ? 'yes' : 'no'} />
                    </Grid>
                    {reviewCase.cohort?.course?.id === CoursesIds.CPH_ID && (
                        <Grid item xs={6} sm={4} md={2}>
                            <MetaField label='Laser crystal' value={reviewCase.crystal_used === 'Y' ? 'yes' : 'no'} />
                        </Grid>
                    )}
                    <Grid item xs={6} sm={4} md={2}>
                        <MetaField
                            label='Created'
                            value={DateTime.fromISO(reviewCase.created_date).toLocaleString(DateTime.DATE_MED)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <MetaField
                            label='Categories'
                            value={
                                reviewCase.categories?.length ? (
                                    <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                                        {reviewCase.categories.map((category) => (
                                            <Chip key={category.id} label={category.name} size='small' variant='outlined' />
                                        ))}
                                    </Stack>
                                ) : null
                            }
                            emptyText='none'
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MetaField label='Purpose' value={<ExpandableText text={reviewCase.purpose} />} />
                    </Grid>
                </Grid>

                {statuses.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Button size='small' onClick={() => setHistoryOpen((open) => !open)} sx={{ px: 0 }}>
                            {historyOpen ? 'Hide status history' : `Status history (${statuses.length})`}
                        </Button>
                        <Collapse in={historyOpen} unmountOnExit>
                            <Box sx={{ mt: 1.5 }}>
                                <StatusHistory statuses={statuses} />
                            </Box>
                        </Collapse>
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default ReviewCaseCard;
